"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { CheckCircle2, Database, LoaderCircle, RefreshCw, Server, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

type MemberProjectProfile = {
  configured: boolean;
  member_email?: string;
  project_ref?: string;
  project_url?: string;
  publishable_key?: string;
  display_name?: string | null;
  connection_status?: "NOT_CONFIGURED" | "CONFIGURED" | "CONNECTED" | "VERIFYING" | "VERIFIED" | "ERROR" | "DISABLED";
  schema_version?: string | null;
  last_verified_at?: string | null;
  last_error?: string | null;
  updated_at?: string | null;
};

type OwnerProjectRow = {
  member_email: string;
  owner_user_id?: string | null;
  project_ref: string;
  project_url: string;
  display_name?: string | null;
  connection_status: string;
  schema_version?: string | null;
  last_verified_at?: string | null;
  last_error?: string | null;
  updated_at?: string | null;
  enrollment_status?: string | null;
  tenant_user_id?: string | null;
  runyi_management_status?: string | null;
  latest_health_status?: string | null;
  latest_drift_status?: string | null;
};

const PROJECT_URL_PATTERN = /^https:\/\/([a-z0-9-]{6,64})\.supabase\.co\/?$/i;
const PUBLISHABLE_KEY_PATTERN = /^sb_publishable_[A-Za-z0-9_-]+$/;

function cleanCopiedValue(value: string): string {
  return value.replace(/[\s\u200B-\u200D\uFEFF]+/g, "");
}

function normalizeProjectUrl(value: string): string {
  return cleanCopiedValue(value).replace(/\/$/, "").toLowerCase();
}

function normalizePublishableKey(value: string): string {
  return cleanCopiedValue(value);
}

function getProjectRef(value: string): string | null {
  return normalizeProjectUrl(value).match(PROJECT_URL_PATTERN)?.[1]?.toLowerCase() ?? null;
}

function formatDate(value?: string | null): string {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
}

export function MemberProjectPanel({
  controlSupabase,
  signedIn,
  isOwner,
}: {
  controlSupabase: SupabaseClient | null;
  signedIn: boolean;
  isOwner: boolean;
}) {
  const [profile, setProfile] = useState<MemberProjectProfile>({ configured: false });
  const [projectUrl, setProjectUrl] = useState("");
  const [publishableKey, setPublishableKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [ownerProjects, setOwnerProjects] = useState<OwnerProjectRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const projectRef = useMemo(() => getProjectRef(projectUrl), [projectUrl]);
  const normalizedPublishableKey = useMemo(() => normalizePublishableKey(publishableKey), [publishableKey]);
  const projectUrlValid = Boolean(projectRef);
  const publishableKeyValid = PUBLISHABLE_KEY_PATTERN.test(normalizedPublishableKey);
  const inputValid = projectUrlValid && publishableKeyValid;

  const loadOwnerProjects = useCallback(async () => {
    if (!controlSupabase || !signedIn || !isOwner) {
      setOwnerProjects([]);
      return;
    }
    const { data, error } = await controlSupabase.rpc("runyi_owner_list_member_projects");
    if (error) {
      setNotice(error.message);
      return;
    }
    setOwnerProjects(Array.isArray(data?.projects) ? data.projects as OwnerProjectRow[] : []);
  }, [controlSupabase, isOwner, signedIn]);

  const loadProfile = useCallback(async () => {
    if (!controlSupabase || !signedIn) {
      setProfile({ configured: false });
      return;
    }

    setLoading(true);
    const { data, error } = await controlSupabase.rpc("runyi_member_project_profile");
    setLoading(false);

    if (error) {
      const missingMigration = error.code === "PGRST202" || /runyi_member_project_profile|schema cache/i.test(error.message);
      setNotice(missingMigration
        ? "Member-project provisioning is not installed in the RUNYI control plane yet."
        : error.message);
      return;
    }

    const next = (data ?? { configured: false }) as MemberProjectProfile;
    setProfile(next);
    if (next.configured) {
      setProjectUrl(next.project_url ?? "");
      setPublishableKey(next.publishable_key ?? "");
      setDisplayName(next.display_name ?? "");
    }

    await loadOwnerProjects();
  }, [controlSupabase, loadOwnerProjects, signedIn]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  async function saveProfile() {
    if (!controlSupabase || !signedIn) {
      setNotice("Sign in to RUNYI before connecting a member Supabase project.");
      return;
    }

    const normalizedUrl = normalizeProjectUrl(projectUrl);
    if (!PROJECT_URL_PATTERN.test(normalizedUrl)) {
      setNotice("Enter the Supabase HTTPS project URL, for example https://project-ref.supabase.co.");
      return;
    }
    const normalizedKey = normalizePublishableKey(publishableKey);
    if (!PUBLISHABLE_KEY_PATTERN.test(normalizedKey)) {
      setNotice("Use only the full sb_publishable_... key. RUNYI removes accidental spaces from copied values automatically.");
      return;
    }

    setLoading(true);
    setNotice(null);
    const { data, error } = await controlSupabase.functions.invoke("runyi-member-control", {
      body: {
        operation: "member_project_upsert",
        project_url: normalizedUrl,
        publishable_key: normalizedKey,
        display_name: displayName.trim() || null,
      },
    });
    setLoading(false);

    const errorMessage = error?.message ?? (data?.error ? String(data.error) : null);
    if (errorMessage) {
      setNotice(errorMessage);
      return;
    }

    const savedProfile = (data?.profile ?? { configured: true }) as MemberProjectProfile;
    setProfile(savedProfile);
    window.dispatchEvent(new CustomEvent("runyi:member-project-profile-changed"));
    setNotice("Member Supabase project saved to your RUNYI connection profile. Test the project next.");
    await loadOwnerProjects();
  }

  async function testProject() {
    if (!controlSupabase || !signedIn) {
      setNotice("Sign in to RUNYI first.");
      return;
    }

    const normalizedUrl = normalizeProjectUrl(projectUrl);
    const key = normalizePublishableKey(publishableKey);
    if (!PROJECT_URL_PATTERN.test(normalizedUrl) || !PUBLISHABLE_KEY_PATTERN.test(key)) {
      setNotice("Save a valid Supabase project URL and publishable key first.");
      return;
    }

    if (
      !profile.configured
      || normalizeProjectUrl(profile.project_url ?? "") !== normalizedUrl
      || normalizePublishableKey(profile.publishable_key ?? "") !== key
    ) {
      setNotice("Save this exact project connection first, then run the authoritative verification.");
      return;
    }

    setTesting(true);
    setNotice(null);

    try {
      const { data, error } = await controlSupabase.functions.invoke("runyi-project-verify", {
        body: {},
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));

      await loadProfile();
      window.dispatchEvent(new CustomEvent("runyi:member-project-profile-changed"));
      const schemaVersion = String(data?.schema_version ?? "unknown");
      setNotice(`RUNYI backend verified this project directly. Schema ${schemaVersion} on ${data?.project_ref ?? projectRef ?? "the member project"}.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "The member Supabase project could not be verified.";
      setNotice(
        /runyi_member_health|schema cache|function|bootstrap/i.test(message)
          ? "The project is reachable, but its RUNYI health contract is missing. Install the required member migration, then verify again."
          : message,
      );
    } finally {
      setTesting(false);
      await loadOwnerProjects();
    }
  }

  if (!signedIn) {
    return (
      <Card className="border-foreground/10 bg-card py-0">
        <CardContent className="p-4 sm:p-7">
          <div className="flex items-start gap-4">
            <div className="grid size-8 shrink-0 place-items-center rounded-lg bg-foreground/6 text-foreground/50"><Server className="size-3.5" /></div>
            <div>
              <p className="font-semibold">Member-owned Supabase project</p>
              <p className="mt-1 text-sm leading-6 text-foreground/48">Sign in to RUNYI first. Then connect the Supabase project that belongs to this member.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <Card className="border-foreground/10 bg-card py-0">
        <CardContent className="space-y-4 p-4 sm:space-y-5 sm:p-7">
          <div className="flex items-start gap-4">
            <div className={`grid size-8 shrink-0 place-items-center rounded-lg ${profile.connection_status === "VERIFIED" ? "bg-emerald-400/10 text-emerald-300" : "bg-primary/10 text-primary"}`}>
              <Server className="size-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold">3. Connect your Supabase project</p>
                <Badge className={profile.connection_status === "VERIFIED" ? "bg-emerald-400/10 text-emerald-300" : profile.connection_status === "ERROR" ? "bg-rose-400/10 text-rose-200" : "bg-foreground/8 text-foreground/65"}>
                  {profile.connection_status ?? "Not configured"}
                </Badge>
              </div>
              <p className="mt-1 text-sm leading-6 text-foreground/48">
                Each RUNYI member can own a separate Supabase project. RUNYI stores the project URL and publishable client key for normal app routing. Authoritative verification runs through the trusted RUNYI backend; privileged management authorization is a separate server-side channel.
              </p>
            </div>
          </div>

          <div className="grid gap-4">
            <label className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground/45">Project label · optional</span>
              <Input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="My RUNYI movie database"
                className="h-12 border-foreground/12 bg-[var(--surface-inset)] text-[16px]"
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground/45">Supabase project URL</span>
              <Input
                value={projectUrl}
                onChange={(event) => setProjectUrl(event.target.value)}
                placeholder="https://project-ref.supabase.co"
                autoCapitalize="none"
                autoCorrect="off"
                className="h-12 border-foreground/12 bg-[var(--surface-inset)] text-[16px]"
              />
              {projectRef
                ? <p className="text-xs text-emerald-300">Project URL recognized · ref {projectRef}</p>
                : projectUrl.trim()
                  ? <p className="text-xs text-rose-200">Project URL not recognized. Paste the complete https://...supabase.co URL.</p>
                  : null}
            </label>
            <label className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground/45">Publishable key</span>
              <Input
                type="password"
                value={publishableKey}
                onChange={(event) => setPublishableKey(event.target.value)}
                placeholder="sb_publishable_..."
                autoCapitalize="none"
                autoCorrect="off"
                className="h-12 border-foreground/12 bg-[var(--surface-inset)] font-mono text-[14px]"
              />
              {publishableKey
                ? <p className={`text-xs leading-5 ${publishableKeyValid ? "text-emerald-300" : "text-rose-200"}`}>
                    {publishableKeyValid
                      ? "Publishable key recognized."
                      : "Key not recognized. Copy the complete default key beginning sb_publishable_ from Supabase → API Keys."}
                  </p>
                : <p className="text-xs leading-5 text-foreground/40">RUNYI rejects sb_secret_ and service-role credentials. Use the member project's publishable key only.</p>}
            </label>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              variant="outline"
              onClick={saveProfile}
              disabled={loading || testing || !inputValid}
              className="h-11 border-foreground/12 bg-foreground/5 text-foreground hover:bg-foreground/10 hover:text-foreground"
            >
              {loading ? <LoaderCircle className="animate-spin" /> : <ShieldCheck />}
              Save connection
            </Button>
            <Button
              onClick={testProject}
              disabled={loading || testing || !inputValid}
              className="h-11 bg-primary font-bold text-primary-foreground hover:bg-primary/90"
            >
              {testing ? <LoaderCircle className="animate-spin" /> : profile.connection_status === "VERIFIED" ? <RefreshCw /> : <Database />}
              {testing ? "Testing…" : profile.connection_status === "VERIFIED" ? "Test again" : "Test member project"}
            </Button>
          </div>

          {profile.configured && (
            <div className="rounded-xl border border-foreground/10 bg-[var(--surface-inset)] p-4">
              <div className="flex flex-wrap items-center gap-2">
                {profile.connection_status === "VERIFIED" && <CheckCircle2 className="size-4 text-emerald-300" />}
                <p className="text-sm font-semibold">{profile.display_name || profile.project_ref || "Member Supabase project"}</p>
                {profile.schema_version && <Badge variant="outline" className="border-foreground/12 text-foreground/55">{profile.schema_version}</Badge>}
              </div>
              <p className="mt-2 break-all text-xs text-foreground/45">{profile.project_url}</p>
              <p className="mt-2 text-xs text-foreground/40">Last verified: {formatDate(profile.last_verified_at)}</p>
              {profile.last_error && <p className="mt-2 text-xs leading-5 text-rose-200">{profile.last_error}</p>}
            </div>
          )}

          {notice && <div className="rounded-xl border border-amber-300/20 bg-amber-300/8 p-4 text-sm leading-6 text-amber-100">{notice}</div>}

          <p className="text-xs leading-5 text-foreground/38">
            RUNYI verifies the member-owned project from the trusted backend. Production movie/workflow data routes only to a VERIFIED supported tenant schema; privileged database management uses a separate audited management channel.
          </p>
        </CardContent>
      </Card>

      {isOwner && (
        <Card className="border-foreground/10 bg-card py-0">
          <CardContent className="space-y-4 p-5 sm:p-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-semibold">Member project registry</p>
                <p className="mt-1 text-sm leading-6 text-foreground/48">Owner-only support view. Publishable keys are intentionally not returned here.</p>
              </div>
              <Button variant="outline" onClick={loadOwnerProjects} className="h-10 border-foreground/12 bg-transparent text-foreground hover:bg-foreground/8 hover:text-foreground"><RefreshCw />Refresh</Button>
            </div>
            {ownerProjects.length === 0 ? (
              <p className="rounded-xl border border-dashed border-foreground/12 p-4 text-sm text-foreground/45">No member-owned Supabase projects are registered yet.</p>
            ) : (
              <div className="space-y-2">
                {ownerProjects.map((item) => (
                  <div key={item.member_email} className="rounded-xl border border-foreground/9 bg-[var(--surface-inset)] p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="break-all text-sm font-semibold">{item.member_email}</p>
                      <Badge className={item.connection_status === "VERIFIED" ? "bg-emerald-400/10 text-emerald-300" : item.connection_status === "ERROR" ? "bg-rose-400/10 text-rose-200" : "bg-foreground/8 text-foreground/60"}>{item.connection_status}</Badge>
                    </div>
                    <p className="mt-2 break-all text-xs text-foreground/45">{item.project_ref} · {item.project_url}</p>
                    <p className="mt-1 text-xs text-foreground/38">Schema {item.schema_version ?? "not verified"} · checked {formatDate(item.last_verified_at)}</p>
                    <p className="mt-1 text-xs text-foreground/38">Enrollment {item.enrollment_status ?? "PENDING"} · RUNYI management {item.runyi_management_status ?? "NOT_CONNECTED"}</p>
                    {(item.latest_health_status || item.latest_drift_status) && (
                      <p className="mt-1 text-xs text-foreground/38">Health {item.latest_health_status ?? "UNKNOWN"} · drift {item.latest_drift_status ?? "UNKNOWN"}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
